import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Scale, Trash2 } from 'lucide-react';
import type { LeavePolicyDto, LeaveTypeDto } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { ruleLines, useLeavePolicies, useLeaveTypes } from '@/lib/leave';
import { useReference } from '@/lib/queries';
import { t } from '@/lib/i18n';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Tabs, Textarea, Toggle } from '@/components/ui';

type Tab = 'types' | 'policies';

export function LeaveSetupPage() {
  const [tab, setTab] = useState<Tab>('types');
  return (
    <div>
      <Link to="/leave" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {t('Leave')}
      </Link>
      <PageHeader title={t('Leave types & policies')} subtitle={t('Leave types define the rules; policies define how many days each group of employees gets per year.')} />
      <Tabs
        tabs={[
          { value: 'types', label: t('Leave types') },
          { value: 'policies', label: t('Leave policies') },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'types' ? <TypesTab /> : <PoliciesTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leave types
// ---------------------------------------------------------------------------
type NumKey = 'maxDaysPerRequest' | 'maxDaysPerYear' | 'maxConsecutiveDays' | 'minGapDays' | 'minServiceMonths' | 'maxTimesPerCareer';
type TypeForm = Omit<LeaveTypeDto, 'id' | 'inUse' | 'code' | 'description' | NumKey> & {
  id?: string;
  code: string;
  description: string;
} & Record<NumKey, string>;
const NUM_KEYS: NumKey[] = ['maxDaysPerRequest', 'maxDaysPerYear', 'maxConsecutiveDays', 'minGapDays', 'minServiceMonths', 'maxTimesPerCareer'];
const num = (v: string) => (v.trim() ? Number(v) : null);
const blankType: TypeForm = {
  name: '',
  code: '',
  description: '',
  isPaid: true,
  requiresApproval: true,
  deductsBalance: true,
  allowNegativeBalance: false,
  allowHalfDays: true,
  maxDaysPerRequest: '',
  maxDaysPerYear: '',
  maxConsecutiveDays: '',
  minGapDays: '',
  minServiceMonths: '',
  maxTimesPerCareer: '',
  requiresDocument: false,
  countsCalendarDays: false,
  drawsFromTypeId: null,
  applicableCountryCodes: null,
  color: '#3372fb',
  isPrimary: false,
  sortOrder: 100,
  isActive: true,
};

function TypesTab() {
  const qc = useQueryClient();
  const types = useLeaveTypes();
  const ref = useReference();
  const [f, setF] = useState<TypeForm | null>(null);
  const [del, setDel] = useState<LeaveTypeDto | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f!.name,
        code: f!.code || null,
        description: f!.description || null,
        isPaid: f!.isPaid,
        requiresApproval: f!.requiresApproval,
        deductsBalance: f!.deductsBalance,
        allowNegativeBalance: f!.allowNegativeBalance,
        allowHalfDays: f!.allowHalfDays,
        maxDaysPerRequest: num(f!.maxDaysPerRequest),
        maxDaysPerYear: num(f!.maxDaysPerYear),
        maxConsecutiveDays: num(f!.maxConsecutiveDays),
        minGapDays: num(f!.minGapDays),
        minServiceMonths: num(f!.minServiceMonths),
        maxTimesPerCareer: num(f!.maxTimesPerCareer),
        requiresDocument: f!.requiresDocument,
        countsCalendarDays: f!.countsCalendarDays,
        drawsFromTypeId: f!.drawsFromTypeId || null,
        applicableCountryCodes: f!.applicableCountryCodes?.length ? f!.applicableCountryCodes : null,
        color: f!.color,
        isPrimary: f!.isPrimary,
        sortOrder: Number(f!.sortOrder),
        ...(f!.id ? { isActive: f!.isActive } : {}),
      };
      return f!.id ? api.patch(`/leave-types/${f!.id}`, body) : api.post('/leave-types', body);
    },
    onSuccess: () => {
      setF(null);
      void qc.invalidateQueries({ queryKey: ['leave-types'] });
      void qc.invalidateQueries({ queryKey: ['me', 'leave-types'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/leave-types/${id}`),
    onSuccess: () => {
      setDel(null);
      void qc.invalidateQueries({ queryKey: ['leave-types'] });
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const set = <K extends keyof TypeForm>(k: K, v: TypeForm[K]) => setF((s) => (s ? { ...s, [k]: v } : s));
  const [presetOpen, setPresetOpen] = useState(false);
  const preset = useMutation({
    mutationFn: () => api.post<{ created: string[]; updated: string[]; policy: string }>('/leave-setup/egypt-preset', {}),
    onSuccess: () => {
      setPresetOpen(false);
      void qc.invalidateQueries({ queryKey: ['leave-types'] });
      void qc.invalidateQueries({ queryKey: ['leave-policies'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <>
      {preset.data && (
        <div className="mb-4">
          <Notice tone="green">
            {t('Egyptian labour law applied: {created} new and {updated} updated leave types, policy "{policy}".', {
              created: preset.data.created.length,
              updated: preset.data.updated.length,
              policy: t(preset.data.policy),
            })}
          </Notice>
        </div>
      )}
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" icon={<Scale className="h-4 w-4" />} onClick={() => { preset.reset(); setPresetOpen(true); }}>
          {t('Apply Egyptian labour law')}
        </Button>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => { save.reset(); setF({ ...blankType }); }}>
          {t('New leave type')}
        </Button>
      </div>
      <ErrorBanner error={types.error} />
      <Card bodyClass="p-0">
        {types.isLoading ? (
          <Spinner />
        ) : !types.data?.length ? (
          <EmptyState title={t('No leave types yet')} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {types.data.map((lt) => (
              <li key={lt.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: lt.color }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {t(lt.name)} {lt.code && <span className="text-xs text-slate-400">({lt.code})</span>}{' '}
                      {lt.isPrimary && <Badge tone="brand">{t('Main vacation type')}</Badge>} {!lt.isActive && <Badge>{t('Inactive')}</Badge>}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge tone={lt.isPaid ? 'green' : 'slate'}>{lt.isPaid ? t('Paid') : t('Unpaid')}</Badge>
                      <Badge>{lt.requiresApproval ? t('Needs approval') : t('Auto-approved')}</Badge>
                      <Badge>{lt.deductsBalance ? t('Uses balance') : t('No balance')}</Badge>
                      {lt.allowNegativeBalance && <Badge tone="amber">{t('Negative allowed')}</Badge>}
                      {!lt.allowHalfDays && <Badge>{t('Full days only')}</Badge>}
                      {lt.maxDaysPerRequest && <Badge>{t('Max {n}/request', { n: lt.maxDaysPerRequest })}</Badge>}
                      {lt.maxDaysPerYear && <Badge>{t('Max {n}/year', { n: lt.maxDaysPerYear })}</Badge>}
                      {lt.applicableCountryCodes && <Badge tone="violet">{t('Only {countries}', { countries: lt.applicableCountryCodes.join(', ') })}</Badge>}
                      {ruleLines({ ...lt, maxDaysPerYear: null }, types.data?.find((x) => x.id === lt.drawsFromTypeId)?.name).map((r) => (
                        <Badge key={r} tone="brand">
                          {r}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      save.reset();
                      setF({
                        ...lt,
                        code: lt.code ?? '',
                        description: lt.description ?? '',
                        ...(Object.fromEntries(NUM_KEYS.map((k) => [k, lt[k] ? String(lt[k]) : ''])) as Record<NumKey, string>),
                      });
                    }}
                  >
                    {t('Edit')}
                  </Button>
                  {!lt.inUse && (
                    <Button variant="ghost" size="sm" onClick={() => setDel(lt)}>
                      {t('Delete')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={!!f}
        onClose={() => setF(null)}
        title={f?.id ? t('Edit leave type') : t('New leave type')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setF(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} disabled={!f?.name.trim()} onClick={() => save.mutate()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        {f && (
          <div className="space-y-4">
            <ErrorBanner error={save.error} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label={t('Name')} required error={fe.name} className="sm:col-span-2">
                <Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder={t('e.g. Annual Vacation')} />
              </Field>
              <Field label={t('Code')} error={fe.code} hint={t('Optional, e.g. AL')}>
                <Input value={f.code} onChange={(e) => set('code', e.target.value.toUpperCase())} maxLength={12} />
              </Field>
              <Field label={t('Description')} className="sm:col-span-3">
                <Textarea rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-2">
              <Toggle checked={f.isPaid} onChange={(v) => set('isPaid', v)} label={t('Paid')} />
              <Toggle checked={f.requiresApproval} onChange={(v) => set('requiresApproval', v)} label={t('Requires approval')} description={t('Off = approved automatically when submitted.')} />
              <Toggle checked={f.deductsBalance} onChange={(v) => set('deductsBalance', v)} label={t('Deducts from balance')} description={t('Off for e.g. unpaid leave.')} />
              <Toggle
                checked={f.allowNegativeBalance}
                onChange={(v) => set('allowNegativeBalance', v)}
                label={t('Allow negative balance')}
                description={t('Employees may request more than they have.')}
                disabled={!f.deductsBalance}
              />
              <Toggle checked={f.allowHalfDays} onChange={(v) => set('allowHalfDays', v)} label={t('Allow half days')} />
              <Toggle checked={f.isPrimary} onChange={(v) => set('isPrimary', v)} label={t('Main vacation type')} description={t("Shown as 'Vacation balance' on dashboards.")} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label={t('Max days per request')} error={fe.maxDaysPerRequest} hint={t('Empty = no limit')}>
                <Input type="number" step="0.5" min={0} value={f.maxDaysPerRequest} onChange={(e) => set('maxDaysPerRequest', e.target.value)} />
              </Field>
              <Field label={t('Max days per leave year')} error={fe.maxDaysPerYear} hint={t('Empty = no limit')}>
                <Input type="number" step="0.5" min={0} value={f.maxDaysPerYear} onChange={(e) => set('maxDaysPerYear', e.target.value)} />
              </Field>
              <Field label={t('Colour')}>
                <Input type="color" value={f.color} onChange={(e) => set('color', e.target.value)} className="h-10 p-1" />
              </Field>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="mb-3 text-sm font-semibold text-slate-800">{t('Rules')}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t('Most days in a row')} error={fe.maxConsecutiveDays} hint={t('Empty = no limit (e.g. 2 for sick leave without a report)')}>
                  <Input type="number" step="0.5" min={0} value={f.maxConsecutiveDays} onChange={(e) => set('maxConsecutiveDays', e.target.value)} />
                </Field>
                <Field label={t('Days needed between two requests')} error={fe.minGapDays} hint={t('Empty = none (e.g. 7 = one week)')}>
                  <Input type="number" step="1" min={0} value={f.minGapDays} onChange={(e) => set('minGapDays', e.target.value)} />
                </Field>
                <Field label={t('Available after (months of service)')} error={fe.minServiceMonths} hint={t('Empty = from the first day (e.g. 3 for annual leave)')}>
                  <Input type="number" step="1" min={0} value={f.minServiceMonths} onChange={(e) => set('minServiceMonths', e.target.value)} />
                </Field>
                <Field label={t('Times allowed during the employment')} error={fe.maxTimesPerCareer} hint={t('Empty = no limit (e.g. 1 for Hajj)')}>
                  <Input type="number" step="1" min={0} value={f.maxTimesPerCareer} onChange={(e) => set('maxTimesPerCareer', e.target.value)} />
                </Field>
                <Field label={t('Take the days from another balance')} error={fe.drawsFromTypeId} hint={t('e.g. emergency leave comes out of annual vacation')}>
                  <Select value={f.drawsFromTypeId ?? ''} onChange={(e) => set('drawsFromTypeId', e.target.value || null)}>
                    <option value="">{t('No — its own balance')}</option>
                    {(types.data ?? [])
                      .filter((x) => x.id !== f.id && x.deductsBalance && !x.drawsFromTypeId)
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {t(x.name)}
                        </option>
                      ))}
                  </Select>
                </Field>
                <div className="space-y-3">
                  <Toggle checked={f.requiresDocument} onChange={(v) => set('requiresDocument', v)} label={t('Medical report required')} description={t('A PDF or photo must be attached to every request.')} />
                  <Toggle checked={f.countsCalendarDays} onChange={(v) => set('countsCalendarDays', v)} label={t('Count calendar days')} description={t('Weekends and holidays count too (e.g. maternity leave).')} />
                </div>
              </div>
            </div>
            <Field label={t('Only for employees in these countries')} hint={t('Leave empty for all countries. Hold Ctrl/Cmd to select several.')}>
              <Select
                multiple
                value={f.applicableCountryCodes ?? []}
                onChange={(e) => set('applicableCountryCodes', Array.from(e.target.selectedOptions).map((o) => o.value))}
                className="h-32"
              >
                {ref.data?.countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            {f.id && <Toggle checked={f.isActive} onChange={(v) => set('isActive', v)} label={t('Active')} description={t("Inactive types can't be requested; history is kept.")} />}
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={presetOpen}
        onClose={() => setPresetOpen(false)}
        title={t('Apply Egyptian labour law?')}
        message={t(
          'Creates or updates these leave types with their rules: annual vacation (15 / 21 / 30 / 45 days, not in the first 3 months), emergency (7 days, from annual), sick leave without a report (5 days, at most 2 in a row, a week apart), sick leave with a medical report, unpaid, marriage, maternity, paternity, bereavement and Hajj. The "Egypt — standard" policy is set to the law and each person\'s leave year starts on their start date. Existing balances keep their figures.',
        )}
        confirmLabel={t('Apply')}
        loading={preset.isPending}
        error={preset.error}
        onConfirm={() => preset.mutate()}
      />
      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title={t('Delete {name}?', { name: del ? t(del.name) : '' })}
        message={t('This leave type has never been used, so it can be deleted permanently.')}
        confirmLabel={t('Delete')}
        danger
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Policies
// ---------------------------------------------------------------------------
interface PolicyForm {
  id?: string;
  name: string;
  description: string;
  isActive: boolean;
  entitlements: { leaveTypeId: string; daysPerYear: string; accrualMethod: 'upfront' | 'monthly'; carryOverMaxDays: string; rule: 'fixed' | 'egypt_annual' }[];
}

function PoliciesTab() {
  const qc = useQueryClient();
  const policies = useLeavePolicies();
  const types = useLeaveTypes();
  const [f, setF] = useState<PolicyForm | null>(null);
  const [del, setDel] = useState<LeavePolicyDto | null>(null);
  const deducting = (types.data ?? []).filter((lt) => lt.deductsBalance && !lt.drawsFromTypeId);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f!.name,
        description: f!.description || null,
        ...(f!.id ? { isActive: f!.isActive } : {}),
        entitlements: f!.entitlements
          .filter((e) => e.leaveTypeId)
          .map((e) => ({ leaveTypeId: e.leaveTypeId, daysPerYear: Number(e.daysPerYear || 0), accrualMethod: e.accrualMethod, carryOverMaxDays: Number(e.carryOverMaxDays || 0), rule: e.rule })),
      };
      return f!.id ? api.put(`/leave-policies/${f!.id}`, body) : api.post('/leave-policies', body);
    },
    onSuccess: () => {
      setF(null);
      void qc.invalidateQueries({ queryKey: ['leave-policies'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/leave-policies/${id}`),
    onSuccess: () => {
      setDel(null);
      void qc.invalidateQueries({ queryKey: ['leave-policies'] });
    },
  });
  const setEnt = (i: number, patch: Partial<PolicyForm['entitlements'][number]>) =>
    setF((s) => (s ? { ...s, entitlements: s.entitlements.map((e, j) => (j === i ? { ...e, ...patch } : e)) } : s));

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => {
            save.reset();
            setF({ name: '', description: '', isActive: true, entitlements: deducting.map((lt) => ({ leaveTypeId: lt.id, daysPerYear: '', accrualMethod: 'upfront', carryOverMaxDays: '0', rule: 'fixed' })) });
          }}
        >
          {t('New policy')}
        </Button>
      </div>
      <ErrorBanner error={policies.error} />
      {policies.isLoading ? (
        <Spinner />
      ) : !policies.data?.length ? (
        <Card>
          <EmptyState title={t('No leave policies yet')} description={t('Create one per country or contract type, then assign it to employees.')} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {policies.data.map((p) => (
            <Card
              key={p.id}
              title={
                <span>
                  {t(p.name)} {!p.isActive && <Badge>{t('Inactive')}</Badge>}
                  <span className="ms-2 text-xs font-normal text-slate-500">{p.employeeCount === 1 ? t('{n} employee', { n: p.employeeCount }) : t('{n} employees', { n: p.employeeCount })}</span>
                </span>
              }
              actions={
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      save.reset();
                      setF({
                        id: p.id,
                        name: p.name,
                        description: p.description ?? '',
                        isActive: p.isActive,
                        entitlements: p.entitlements.map((e) => ({ leaveTypeId: e.leaveTypeId, daysPerYear: String(e.daysPerYear), accrualMethod: e.accrualMethod, carryOverMaxDays: String(e.carryOverMaxDays), rule: e.rule })),
                      });
                    }}
                  >
                    {t('Edit')}
                  </Button>
                  {p.employeeCount === 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setDel(p)}>
                      {t('Delete')}
                    </Button>
                  )}
                </div>
              }
            >
              {p.description && <p className="mb-3 text-xs text-slate-500">{p.description}</p>}
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {p.entitlements.map((e) => (
                    <tr key={e.leaveTypeId}>
                      <td className="py-1.5 text-slate-700">{t(e.leaveTypeName)}</td>
                      <td className="py-1.5 text-end font-medium">{e.rule === 'egypt_annual' ? t('15 / 21 / 30 / 45 days (Egyptian law)') : t('{n} days', { n: e.daysPerYear })}</td>
                      <td className="py-1.5 ps-3 text-end text-xs text-slate-500">
                        {e.accrualMethod === 'monthly' ? t('monthly') : t('up front')}
                        {e.carryOverMaxDays ? ` · ${t('carry ≤ {n}', { n: e.carryOverMaxDays })}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={!!f}
        onClose={() => setF(null)}
        title={f?.id ? t('Edit leave policy') : t('New leave policy')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setF(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} disabled={!f?.name.trim()} onClick={() => save.mutate()}>
              {t('Save policy')}
            </Button>
          </>
        }
      >
        {f && (
          <div className="space-y-4">
            <ErrorBanner error={save.error} />
            {f.id && (
              <Notice tone="amber">
                {t('Changes apply to leave years created from now on. Balances that already exist keep their allowance — change individual balances on the Vacation Balances page if needed.')}
              </Notice>
            )}
            <Field label={t('Name')} required>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('e.g. Egypt — standard')} />
            </Field>
            <Field label={t('Description')}>
              <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            </Field>
            <div>
              <p className="label">{t('Allowances per leave year')}</p>
              <div className="space-y-2">
                {f.entitlements.map((e, i) => (
                  <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-lg border border-slate-200 p-2">
                    <Field label={t('Leave type')} className="col-span-12 sm:col-span-4">
                      <Select value={e.leaveTypeId} onChange={(ev) => setEnt(i, { leaveTypeId: ev.target.value })}>
                        <option value="">{t('Select…')}</option>
                        {deducting.map((lt) => (
                          <option key={lt.id} value={lt.id}>
                            {t(lt.name)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('Days / year')} className="col-span-4 sm:col-span-2">
                      <Select value={e.rule === 'egypt_annual' ? 'egypt' : 'fixed'} onChange={(ev) => setEnt(i, { rule: ev.target.value === 'egypt' ? 'egypt_annual' : 'fixed' })}>
                        <option value="fixed">{t('Fixed number')}</option>
                        <option value="egypt">{t('Egyptian law')}</option>
                      </Select>
                      {e.rule !== 'egypt_annual' && (
                        <Input className="mt-1" type="number" step="0.5" min={0} value={e.daysPerYear} onChange={(ev) => setEnt(i, { daysPerYear: ev.target.value })} />
                      )}
                    </Field>
                    <Field label={t('Earned')} className="col-span-4 sm:col-span-3">
                      <Select value={e.accrualMethod} onChange={(ev) => setEnt(i, { accrualMethod: ev.target.value as 'upfront' | 'monthly' })}>
                        <option value="upfront">{t('All at start of year')}</option>
                        <option value="monthly">{t('Monthly')}</option>
                      </Select>
                    </Field>
                    <Field label={t('Max carry-over')} className="col-span-3 sm:col-span-2">
                      <Input type="number" step="0.5" min={0} value={e.carryOverMaxDays} onChange={(ev) => setEnt(i, { carryOverMaxDays: ev.target.value })} />
                    </Field>
                    <div className="col-span-1 pb-1">
                      <Button variant="ghost" size="sm" aria-label={t('Remove')} onClick={() => setF({ ...f, entitlements: f.entitlements.filter((_, j) => j !== i) })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setF({ ...f, entitlements: [...f.entitlements, { leaveTypeId: '', daysPerYear: '', accrualMethod: 'upfront', carryOverMaxDays: '0', rule: 'fixed' }] })}
              >
                {t('Add leave type')}
              </Button>
            </div>
            {f.id && <Toggle checked={f.isActive} onChange={(v) => setF({ ...f, isActive: v })} label={t('Active')} />}
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title={t('Delete {name}?', { name: del ? t(del.name) : '' })}
        message={t('Policies that are or were assigned to employees are kept for history and can only be deactivated.')}
        confirmLabel={t('Delete')}
        danger
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </>
  );
}
